import {graphNode} from "@cm/graph-v3"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {z} from "zod/v4-mini"
import {InputNode} from "#template/fundamental/input/input-node"
import type {InputNodeDefinition, InputNodeParameters} from "#template/fundamental/input/types"
import {inputNodeParameters} from "#template/fundamental/input/validation"
import {OutputNode} from "#template/fundamental/output/output-node"
import type {OutputNodeDefinition, OutputNodeParameters} from "#template/fundamental/output/types"
import {outputNodeParameters} from "#template/fundamental/output/validation"
import {SwitchNode} from "#template/fundamental/switch/switch-node"
import type {SwitchNodeDefinition, SwitchNodeParameters} from "#template/fundamental/switch/types"
import {switchNodeParameters} from "#template/fundamental/switch/validation"
import {ExportNode} from "#template/fundamental/export/export-node"
import type {ExportNodeDefinition, ExportNodeParameters} from "#template/fundamental/export/types"
import {exportNodeParameters} from "#template/fundamental/export/validation"
import {LikesListNode} from "#template/fundamental/switch/likes-list-node"
import type {TemplateListNodeDefinition, TemplateListNodeParameters} from "#template/template-list-node/types"
import {getTemplateListNodeReturns} from "#template/template-list-node/validation"
import {getTemplateNodeReturns} from "#template/template-node/validation"
import type {EvaluableTemplateNode} from "#template/evaluable-template-node"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import {TemplateNode} from "#template/template-node/template-node"
import type {InterfaceDescriptor} from "#template/interface-descriptors"
import type {FundamentalLikeNode, LikeMarker, LikeNodeOf} from "#template/role-markers"

export type InfoClassCtor = new (...args: any[]) => InterfaceDescriptor<unknown>

// Unconstrained: when T is a `TNode | TRaw` union (primitive FTs), distribution gives `R | never = R`.
export type EvaluatedValueOf<T> = T extends EvaluableTemplateNode<infer V> ? V : never

type CtorTo<I, P> = (new (options: {parameters: P; disableValidation?: boolean}) => I) & {getNodeClass(): string}

export type FtEvaluatedInput<K extends string, R> = {
    readonly type: K
    readonly ancestry: readonly string[]
    readonly value: R
    readonly origin?: TemplateNode
}

// Breaks the cycle with `NodeEvaluator`.
export interface FundamentalTypeEvaluator {
    evaluate<R>(node: EvaluableTemplateNode<R> | NoInfer<R>): BuilderInlet<R>
}

// `TLike` is the FT's full item type — `TNode | TRaw` for primitive FTs, just `TNode` for
// object-typed ones. `LikeNodeOf<TLike>` projects back to the node-only side for containers that
// only hold nodes (Likes/Switch/Output).
export interface FundamentalType<TLike, K extends string = string> {
    name: K
    // `[name, ...parent.ancestry]`. Mesh has `["Mesh", "Object"]`. Used to fan-out Export writes and
    // widen resolveTemplateInput's discriminator so a descendant FT's entry satisfies an ancestor.
    ancestry: readonly string[]
    isLike: (x: unknown) => x is TLike
    zodLike: z.ZodMiniType<TLike, TLike>
    markers: readonly symbol[]
    InfoClass: InfoClassCtor
    icon: string
    makeEvaluatedInput: (
        scope: GraphBuilderScope,
        evaluator: FundamentalTypeEvaluator,
        node: unknown,
        origin: TemplateNode | undefined,
    ) => BuilderInlet<FtEvaluatedInput<K, EvaluatedValueOf<TLike>>> | null
    Likes: CtorTo<
        LikesListNode<LikeNodeOf<TLike>> & {get definition(): TemplateListNodeDefinition<LikeNodeOf<TLike>>},
        TemplateListNodeParameters<LikeNodeOf<TLike>>
    >
    Switch: CtorTo<
        LikeNodeOf<TLike> & SwitchNode<LikeNodeOf<TLike>> & {get definition(): SwitchNodeDefinition<LikeNodeOf<TLike>>},
        SwitchNodeParameters<LikeNodeOf<TLike>>
    >
    Input: CtorTo<
        LikeNodeOf<TLike> &
            InputNode<TLike> & {
                get definition(): InputNodeDefinition<TLike>
                get ancestry(): readonly string[]
                get InfoClass(): InfoClassCtor
            },
        InputNodeParameters<TLike>
    >
    Output: CtorTo<
        LikeNodeOf<TLike> &
            OutputNode<EvaluatedValueOf<TLike>> & {
                get definition(): OutputNodeDefinition<EvaluatedValueOf<TLike>>
                get ancestry(): readonly string[]
            },
        OutputNodeParameters<EvaluatedValueOf<TLike>>
    >
    Export: CtorTo<
        ExportNode<TLike> & {
            get definition(): ExportNodeDefinition<TLike>
            get ancestry(): readonly string[]
            get InfoClass(): InfoClassCtor
        },
        ExportNodeParameters<TLike>
    >
    // Constructs a Switch with an empty Likes — avoids the per-FT TLike invariance crashing TS when
    // callers iterate `AnyFundamentalType` (e.g. `entry.Switch` typed as a union).
    createEmptySwitch(name: string): LikeNodeOf<TLike> & SwitchNode<LikeNodeOf<TLike>>
}

type FundamentalNodeKey = "Likes" | "Switch" | "Input" | "Output" | "Export"

export function registerFundamentalNode<TLike, K extends FundamentalNodeKey>(ft: FundamentalType<TLike, string>, key: K) {
    return function (target: FundamentalType<TLike, string>[K]) {
        registerTemplateGraphNode(target)
        ft[key] = target
    }
}

// `TNode` and `TRaw` are inferred from `marker`. Listed as separate generics so TS can satisfy the
// `extends EvaluableTemplateNode<unknown>` constraint on `LikesListNode<TNode>` / `SwitchNode<TNode>`
// inside the body — a distributive `LikeNodeOf<...>` would stay opaque under an unsubstituted union.
export function createFundamentalType<TNode extends FundamentalLikeNode<unknown>, K extends string, TRaw = never>(config: {
    name: K
    marker: LikeMarker<TNode, TRaw>
    parent?: {readonly markers: readonly symbol[]; readonly ancestry: readonly string[]}
    InfoClass: InfoClassCtor
    icon: string
}): FundamentalType<TNode | TRaw, K> {
    const {name, marker: markerBundle, parent, InfoClass, icon} = config
    const {marker, isLike, like, likeNode} = markerBundle
    const markers: readonly symbol[] = parent ? [marker, ...parent.markers] : [marker]
    const ancestry: readonly string[] = parent ? [name, ...parent.ancestry] : [name]

    type TLike = TNode | TRaw
    type R = EvaluatedValueOf<TNode>

    // Only wrap TemplateNode values in a builder Struct — those require deferred evaluation.
    // Raw values are returned as plain literals so they don't allocate a runtime-graph node per
    // parameter (which would otherwise advance the parent scope's idCounter on every recompile
    // and silently invalidate scoped caches like the scene-manager's ThreeMesh cache).
    const makeEvaluatedInput = (
        scope: GraphBuilderScope,
        evaluator: FundamentalTypeEvaluator,
        node: unknown,
        origin: TemplateNode | undefined,
    ): BuilderInlet<FtEvaluatedInput<K, R>> | null => {
        if (!isLike(node)) return null
        if (node instanceof TemplateNode) {
            return scope.struct<FtEvaluatedInput<K, R>>("EvaluatedTemplateInput", {
                type: name,
                ancestry,
                value: evaluator.evaluate<R>(node as EvaluableTemplateNode<R>),
                origin,
            })
        }
        return {type: name, ancestry, value: node as R, origin}
    }

    const likesDefinition: TemplateListNodeDefinition<TNode> = {
        nodeClass: `${name}Likes`,
        validation: {
            parameters: z.object({list: z.array(likeNode)}),
            returns: getTemplateListNodeReturns(),
        },
    }
    abstract class _Likes extends LikesListNode<TNode> {}
    class Likes extends _Likes {
        public override get definition() {
            return likesDefinition
        }
    }

    const switchDefinition: SwitchNodeDefinition<TNode> = {
        nodeClass: `${name}Switch`,
        validation: {
            parameters: switchNodeParameters<TNode>(graphNode(Likes)),
            returns: getTemplateNodeReturns(),
        },
    }
    class Switch extends SwitchNode<TNode> {
        public override get definition() {
            return switchDefinition
        }
    }

    const inputDefinition: InputNodeDefinition<TLike> = {
        nodeClass: `${name}Input`,
        validation: {
            parameters: inputNodeParameters<TLike>(like),
            returns: getTemplateNodeReturns(),
        },
    }
    class Input extends InputNode<TLike> {
        public override get definition() {
            return inputDefinition
        }
        public override get ancestry() {
            return ancestry
        }
        public override get InfoClass() {
            return InfoClass
        }
    }

    const outputDefinition: OutputNodeDefinition<EvaluatedValueOf<TNode>> = {
        nodeClass: `${name}Output`,
        validation: {
            parameters: outputNodeParameters(),
            returns: getTemplateNodeReturns(),
        },
    }
    class Output extends OutputNode<EvaluatedValueOf<TNode>> {
        public override get definition() {
            return outputDefinition
        }
        public override get ancestry() {
            return ancestry
        }
    }

    const exportDefinition: ExportNodeDefinition<TLike> = {
        nodeClass: `${name}Export`,
        validation: {
            parameters: exportNodeParameters<TLike>(like),
            returns: getTemplateNodeReturns(),
        },
    }
    class Export extends ExportNode<TLike> {
        public override get definition() {
            return exportDefinition
        }
        public override get ancestry() {
            return ancestry
        }
        public override get InfoClass() {
            return InfoClass
        }
    }

    for (const cls of [Switch, Input, Output]) {
        for (const m of markers) {
            Object.defineProperty(cls.prototype, m, {value: true, writable: false, enumerable: false, configurable: false})
        }
    }

    const fundamentalType = {
        name,
        ancestry,
        isLike,
        zodLike: like,
        markers,
        InfoClass,
        icon,
        makeEvaluatedInput,
        Likes,
        Switch,
        Input,
        Output,
        Export,
        createEmptySwitch: (switchName: string): Switch =>
            new fundamentalType.Switch({
                parameters: {name: switchName, nodes: new fundamentalType.Likes({parameters: {list: []}})} as SwitchNodeParameters<TNode>,
            }),
    }

    return fundamentalType as unknown as FundamentalType<TLike, K>
}
